import { cn } from "@/lib/utils";

export function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[.+?\]\(.+?\))/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-[var(--color-charcoal)]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return (
        <code
          key={i}
          className="bg-[var(--color-surface)] text-[var(--color-burgundy)] text-[11px] px-1.5 py-0.5 rounded font-mono border border-[var(--color-border)]"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    const link = part.match(/\[(.+?)\]\((.+?)\)/);
    if (link) {
      return (
        <a
          key={i}
          href={link[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-burgundy)] underline decoration-dotted hover:decoration-solid transition-all"
        >
          {link[1]}
        </a>
      );
    }
    return part;
  });
}

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({
  content,
  className,
}: MarkdownRendererProps) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) {
      i++;
      continue;
    }

    if (line.startsWith("## ")) {
      elements.push(
        <h3
          key={i}
          className="text-[15px] font-bold text-[var(--color-charcoal)] mt-5 mb-2 first:mt-0 tracking-tight"
        >
          {renderInline(line.slice(3))}
        </h3>,
      );
      i++;
      continue;
    }

    if (line.startsWith("### ")) {
      elements.push(
        <h4
          key={i}
          className="text-[13px] font-bold text-[var(--color-charcoal)] mt-4 mb-1.5"
        >
          {renderInline(line.slice(4))}
        </h4>,
      );
      i++;
      continue;
    }

    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre
          key={`code-${i}`}
          className="bg-[var(--color-charcoal)] text-[#E8D5B7] text-[12px] rounded-[var(--radius-md)] p-4 my-3 overflow-x-auto font-mono leading-relaxed"
        >
          <code>{codeLines.join("\n")}</code>
        </pre>,
      );
      i++;
      continue;
    }

    // blockquote
    if (line.startsWith("> ")) {
      elements.push(
        <div
          key={i}
          className="border-l-2 border-[var(--color-gold)] pl-3 my-2 text-[13px]  bg-[#FAF5E4] py-2 pr-3 rounded-r-[var(--radius-sm)]"
        >
          {renderInline(line.slice(2))}
        </div>,
      );
      i++;
      continue;
    }

    if (line.includes("|") && line.trim().startsWith("|")) {
      const tableLines: string[] = [line];
      i++;
      while (i < lines.length && lines[i].includes("|")) {
        tableLines.push(lines[i]);
        i++;
      }
      const rows = tableLines.filter((r) => !r.match(/^\|[-\s|]+\|$/));
      elements.push(
        <div
          key={`table-${i}`}
          className="overflow-x-auto my-3 rounded-[var(--radius-md)] border border-[var(--color-border)]"
        >
          <table className="w-full text-[12px] border-collapse">
            <tbody>
              {rows.map((row, ri) => {
                const cells = row
                  .split("|")
                  .filter((c) => c.trim())
                  .map((c) => c.trim());
                const isHeader = ri === 0;
                const Tag = isHeader ? "th" : "td";
                return (
                  <tr
                    key={ri}
                    className={
                      ri % 2 === 0 ? "bg-white" : "bg-[var(--color-surface)]"
                    }
                  >
                    {cells.map((cell, ci) => (
                      <Tag
                        key={ci}
                        className={cn(
                          "px-3 py-2 border-b border-[var(--color-border)] text-left",
                          isHeader &&
                            "bg-[#FAF0F2] font-semibold text-[var(--color-burgundy)] text-[11px] uppercase tracking-wide",
                        )}
                      >
                        {renderInline(cell)}
                      </Tag>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (line.startsWith("- ") || line.startsWith("* ")) {
      const items: string[] = [line.slice(2)];
      i++;
      while (
        i < lines.length &&
        (lines[i].startsWith("- ") || lines[i].startsWith("* "))
      ) {
        items.push(lines[i].slice(2));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="my-2 space-y-1.5">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-[13px] ">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-gold)] mt-[7px] flex-shrink-0" />
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [line.replace(/^\d+\.\s/, "")];
      let num = 2;
      i++;
      while (i < lines.length && new RegExp(`^${num}\\. `).test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s/, ""));
        num++;
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="my-2 space-y-1.5">
          {items.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-[13px] ">
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--color-burgundy)] text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    elements.push(
      <p key={i} className="text-[13px]  leading-[1.75] my-1.5">
        {renderInline(line)}
      </p>,
    );
    i++;
  }

  return <div className={cn("space-y-0.5", className)}>{elements}</div>;
}
